import React, { useState } from 'react';
import { Mail, Send, AlertCircle, CheckCircle } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Simulation d'envoi (frontend only for now)
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <main className="contact-page">
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">Contact</h1>
          <p className="page-subtitle">
            Disponible pour de nouveaux défis. Décrivez votre périmètre (URL / environnements / API / objectifs), 
            je reviens vers vous avec une proposition.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2 className="contact-info-title">Coordonnées</h2>
              
              <div className="contact-item">
                <Mail className="contact-icon" />
                <div>
                  <h3 className="contact-label">Email</h3>
                  <a href="mailto:vianney-s@hotmail.fr" className="contact-link">
                    vianney-s@hotmail.fr
                  </a>
                </div>
              </div>

              <div className="contact-item">
                <svg className="contact-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
                <div>
                  <h3 className="contact-label">LinkedIn</h3>
                  <a 
                    href="https://www.linkedin.com/in/vianneysoulard" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="contact-link"
                  >
                    vianneysoulard
                  </a>
                </div>
              </div>

              <div className="contact-item">
                <svg className="contact-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
                <div>
                  <h3 className="contact-label">Malt</h3>
                  <a 
                    href="https://www.malt.fr/profile/vianneysoulard1" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="contact-link"
                  >
                    vianneysoulard1
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-form-wrapper">
              {isSubmitted ? (
                <div className="success-message">
                  <CheckCircle size={48} />
                  <h3>Message envoyé !</h3>
                  <p>Je vous répondrai dans les plus brefs délais.</p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Nom</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      placeholder="Votre nom"
                    />
                    {errors.name && (
                      <div className="error-message">
                        <AlertCircle size={16} />
                        <span>{errors.name}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="votre@email.com"
                    />
                    {errors.email && (
                      <div className="error-message">
                        <AlertCircle size={16} />
                        <span>{errors.email}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className={`form-textarea ${errors.message ? 'error' : ''}`}
                      placeholder="Décrivez votre besoin : URL, environnements, API, objectifs..."
                      rows="6"
                    />
                    {errors.message && (
                      <div className="error-message">
                        <AlertCircle size={16} />
                        <span>{errors.message}</span>
                      </div>
                    )}
                  </div>

                  <button type="submit" className="btn-submit">
                    <Send size={20} />
                    Envoyer le message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;